import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, FileText, PlayCircle, Facebook, Twitter, Linkedin, Instagram, Globe, Images } from 'lucide-react';
import { opportunitiesAPI } from '../lib/api';
import { normalizeRichText } from '../lib/richText';
import { BACKEND_URL as API, IS_FOREIGN_HOST } from '../lib/config';
import { Lightbox } from '../pages/GalleryPage';
import { useSettings } from '../App';
import { opportunitySkin } from '../lib/opportunitySkin';
import { FundItPanel } from './FundIt';
import { KeyFigures, ActionRow, TierTabs, PeopleList, AuthorBlock, Questions } from './OpportunityDetailExtras';

// ─────────────────────────────────────────────────────────────────────────
// «Moderno» /opportunities/:slug (s141) — approved by Anthony: "Gallery +
// Section Nav". Title on white, photo mosaic (one large + two small, opens the
// /gallery lightbox), a sticky section bar under the fixed navbar that jumps
// through one long page (About, Offerings, Updates, FAQ, Team), and a graphite
// term card that stays in view on the right. Same data and blocks as the
// s153: used by every brand, skinned per theme via lib/opportunitySkin.
// ─────────────────────────────────────────────────────────────────────────

const HEAD = 'var(--op-head)';
const NAV_H = 80;      // fixed navbar (Navbar.js h-20)
const BAR_H = 53;      // section bar height
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const line = 'rgba(20,26,58,.1)';
const faint = 'rgba(20,26,58,.5)';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const money = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const day = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const plain = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
const SOCIAL_ICONS = { facebook: Facebook, twitter: Twitter, linkedin: Linkedin, instagram: Instagram, google: Globe };
const DATE_LABELS = [['funding_end', 'Funding end'], ['launch', 'Launch'], ['project', 'Project'], ['reporting', 'Reporting'], ['distribution', 'Distribution']];

const CSS = `
.mod-prose{font-size:1.0625rem;line-height:1.78;color:var(--color-body-text,#475569);overflow-wrap:break-word}
.mod-prose p{margin:0 0 1.1em}.mod-prose>*:last-child{margin-bottom:0}
.mod-prose strong,.mod-prose b{color:var(--color-heading,#1a2332)}
.mod-prose a{color:var(--op-strong);text-decoration:underline;text-underline-offset:.3em}
.mod-prose img{max-width:100%!important;height:auto!important;border-radius:3px;margin:.4em 0}
.mod-prose ul,.mod-prose ol{padding-left:1.2em;margin:0 0 1.1em}.mod-prose ul{list-style:disc}.mod-prose ol{list-style:decimal}
.mod-prose h1,.mod-prose h2,.mod-prose h3,.mod-prose h4{font-family:var(--op-head);font-weight:600;color:var(--color-heading,#1a2332)!important;line-height:1.25;margin:1.4em 0 .5em}
.mod-tabs::-webkit-scrollbar{display:none}
.mod-faq summary{list-style:none}.mod-faq summary::-webkit-details-marker{display:none}
.mod-faq .mod-plus{transition:transform 200ms cubic-bezier(.23,1,.32,1)}.mod-faq[open] .mod-plus{transform:rotate(45deg)}
.mod-tile img{transition:transform 500ms cubic-bezier(.23,1,.32,1)}
@media (hover:hover) and (pointer:fine){.mod-tile:hover img{transform:scale(1.03)}}
.mod-tile:focus-visible{outline:2px solid var(--color-heading,#1a2332);outline-offset:3px}
@media (prefers-reduced-motion:reduce){.mod-tile img,.mod-faq .mod-plus{transition:none}}
`;

function Rich({ html, className = 'mod-prose' }) {
  if (!html) return null;
  return <div className={className} dangerouslySetInnerHTML={{ __html: normalizeRichText(html) }} />;
}

function SectionTitle({ children }) {
  return <h2 className="font-semibold mb-5" style={{ fontFamily: HEAD, fontSize: '1.6rem', lineHeight: 1.2, color: 'var(--color-heading, #1a2332)' }}>{children}</h2>;
}

/* ── Photo mosaic: one large + two small; the last tile counts the rest. ── */
function Mosaic({ images, open, name, fallback, onOpen }) {
  const chip = (
    <span className="absolute top-3.5 left-3.5 text-[11px] font-bold px-3 py-1 rounded-full"
      style={open ? { background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' } : { background: 'rgba(22,23,26,.75)', color: 'rgba(255,255,255,.85)' }}>
      {open ? 'Open for funding' : 'Funding closed'}
    </span>
  );
  if (!images.length) {
    return (
      <div className="relative aspect-[21/9] rounded-[var(--op-radius)] overflow-hidden" style={{ background: 'var(--op-dark)' }}>
        {fallback ? <img src={img(fallback)} alt="" className="w-full h-full object-cover" />
          : <span className="w-full h-full grid place-items-center px-6 text-center text-4xl font-bold text-white/25" style={{ fontFamily: HEAD }}>{name}</span>}
        {chip}
      </div>
    );
  }
  const Tile = ({ i, className, children }) => (
    <button type="button" onClick={() => onOpen(i)} aria-label={`Open photo ${i + 1} of ${images.length}`}
      className={`mod-tile relative overflow-hidden rounded-[var(--op-radius)] ${className}`} style={{ background: 'var(--op-dark)' }}>
      <img src={img(images[i])} alt="" loading={i ? 'lazy' : 'eager'} className="w-full h-full object-cover" />
      {children}
    </button>
  );
  const rest = images.length - 3;
  return (
    <div className={`grid gap-2.5 h-[clamp(240px,38vw,480px)] ${images.length > 1 ? 'md:grid-cols-[2fr_1fr]' : ''} ${images.length > 2 ? 'md:grid-rows-2' : ''}`}>
      <Tile i={0} className={images.length > 2 ? 'md:row-span-2' : ''}>
        {chip}
        {images.length > 1 && (
          <span className="md:hidden absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-xs font-bold text-white px-3 py-1.5 rounded-full" style={{ background: 'rgba(22,23,26,.75)' }}>
            <Images className="w-3.5 h-3.5" /> {images.length} photos
          </span>
        )}
      </Tile>
      {images.length > 1 && <Tile i={1} className="hidden md:block" />}
      {images.length > 2 && (
        <Tile i={2} className="hidden md:block">
          {rest > 0 && <span className="absolute bottom-3 right-3 text-xs font-bold text-white px-3 py-1.5 rounded-full" style={{ background: 'rgba(22,23,26,.75)' }}>+{rest} photos</span>}
        </Tile>
      )}
    </div>
  );
}

/* ── Sticky section bar: anchors + the section in view underlined. ── */
function SectionBar({ sections, active }) {
  const go = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - NAV_H - BAR_H - 16, behavior: reduce ? 'auto' : 'smooth' });
  };
  return (
    <nav aria-label="Sections" className="sticky z-30 bg-white border-b mt-8" style={{ top: NAV_H, borderColor: line }}>
      <div className={`${shell} flex gap-7 overflow-x-auto overflow-y-hidden mod-tabs`} style={{ scrollbarWidth: 'none' }}>
        {sections.map(s => (
          <a key={s.id} href={`#${s.id}`} onClick={e => go(e, s.id)} aria-current={active === s.id ? 'true' : undefined}
            className="py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-150"
            style={{ color: active === s.id ? 'var(--color-heading, #1a2332)' : faint, borderColor: active === s.id ? 'var(--color-accent, #ff9c1b)' : 'transparent' }}
            data-testid={`opportunity-nav-${s.id}`}>{s.label}</a>
        ))}
      </div>
    </nav>
  );
}

/* Live funding countdown (port of the legacy "My Projects" timer, s153): the
   legacy portal counted down to oportunities_members.end_time, which Follow /
   Fund-it set to the opportunity's funding end date — here that is
   funding_ends_at. Days : Hours : Minutes : Seconds, ticking every second;
   renders nothing once funding has closed. */
function FundingCountdown({ endsAt }) {
  const target = endsAt ? new Date(endsAt).getTime() : NaN;
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!Number.isFinite(target) || target <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!Number.isFinite(target) || target <= now) return null;
  const s = Math.floor((target - now) / 1000);
  const pad = (n) => String(n).padStart(2, '0');
  const parts = [['Days', String(Math.floor(s / 86400))], ['Hours', pad(Math.floor(s / 3600) % 24)], ['Minutes', pad(Math.floor(s / 60) % 60)], ['Seconds', pad(s % 60)]];
  return (
    <div className="my-3 py-3.5 border-y" style={{ borderColor: 'rgba(255,255,255,.12)' }} role="timer" aria-label="Time left to fund" data-testid="opportunity-countdown">
      <span className="block text-[11px] font-semibold uppercase text-white/50 mb-2" style={{ letterSpacing: '.08em' }}>Funding closes in</span>
      <div className="grid grid-cols-4 gap-2 text-center">
        {parts.map(([label, v]) => (
          <div key={label} className="rounded-[var(--op-radius,3px)] py-2.5" style={{ background: 'rgba(255,255,255,.06)' }}>
            <span className="block font-bold text-white tabular-nums" style={{ fontFamily: HEAD, fontSize: '1.65rem', lineHeight: 1.1 }}>{v}</span>
            <span className="block text-[10.5px] font-semibold uppercase text-white/55 mt-1" style={{ letterSpacing: '.08em' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TermCard({ o }) {
  const open = !!o.funding_open;
  const ends = o.funding_ends_at ? new Date(o.funding_ends_at) : null;
  const daysLeft = open && ends ? Math.max(0, Math.ceil((ends - Date.now()) / 86400000)) : null;
  const label = 'text-[11px] font-semibold uppercase text-white/50';
  const dates = DATE_LABELS.filter(([k]) => day(o.dates?.[k]));
  const docs = [
    o.video_url && { href: o.video_url, Icon: PlayCircle, label: 'Watch the video' },
    ...(o.files || []).filter(f => f.url).map(f => ({ href: img(f.url), Icon: FileText, label: f.title || f.url.split('/').pop() })),
    o.pfs_url && { href: img(o.pfs_url), Icon: FileText, label: 'Financial statement' },
  ].filter(Boolean);
  const socials = Object.entries(o.socials || {}).filter(([, v]) => v);
  return (
    <div className="rounded-[var(--op-radius)] p-6 flex flex-col gap-1 text-white/75" style={{ background: 'var(--op-dark)' }} data-testid="opportunity-term-card">
      <span className={label} style={{ letterSpacing: '.08em' }}>Funding goal</span>
      <span className="font-bold text-white tabular-nums" style={{ fontFamily: HEAD, fontSize: '2.4rem', lineHeight: 1.1 }}>{money(o.total_amount)}</span>
      {open && <FundingCountdown endsAt={o.funding_ends_at} />}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 my-3 py-3.5 border-y" style={{ borderColor: 'rgba(255,255,255,.12)' }}>
        {[['Min. ticket', money(o.minimum_investment_amount)],
          open ? ['Days left', daysLeft == null ? '—' : String(daysLeft)] : ['Status', 'Closed'],
          [open ? 'Closes' : 'Closed', day(o.funding_ends_at) || '—'],
          ['Visibility', o.show_mode === 'timer' ? `Timer, ${o.timer_hours} h` : 'Until funding end'],
          ['Published', day(o.published_at) || '—']].map(([k, v], i) => (
          <div key={k} className={i === 4 ? 'col-span-2' : ''}>
            <span className={`block ${label}`} style={{ letterSpacing: '.08em' }}>{k}</span>
            <span className={`block font-bold tabular-nums ${k === 'Days left' ? '' : 'text-white'}`} style={k === 'Days left' ? { color: 'var(--color-accent, #ff9c1b)' } : undefined}>{v}</span>
          </div>
        ))}
      </div>
      {dates.length > 0 && (
        <>
          <h3 className="font-semibold text-white mb-0.5" style={{ fontFamily: HEAD }}>Key dates</h3>
          {dates.map(([k, l]) => (
            <div key={k} className="flex justify-between gap-4 py-1 text-sm">
              <span className={label} style={{ letterSpacing: '.08em' }}>{l}</span>
              <span className="font-bold text-white tabular-nums">{day(o.dates[k])}</span>
            </div>
          ))}
        </>
      )}
      {docs.length > 0 && (
        <>
          <h3 className="font-semibold text-white mt-3 mb-0.5" style={{ fontFamily: HEAD }}>Documents &amp; media</h3>
          {docs.map((d, i) => (
            <a key={i} href={d.href} target="_blank" rel="noopener noreferrer" className="mx-link mx-dark flex items-center gap-2.5 py-1 text-sm font-semibold text-white">
              <d.Icon className="w-4 h-4 flex-none" /><span className="flex-1 min-w-0 truncate">{d.label}</span><ArrowUpRight className="w-4 h-4 flex-none" style={{ color: 'var(--color-accent, #ff9c1b)' }} />
            </a>
          ))}
        </>
      )}
      {socials.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {socials.map(([k, url]) => {
            const Icon = SOCIAL_ICONS[k] || Globe;
            return (
              <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={k}
                className="mx-btn mx-btn--secondary mx-dark w-9 h-9 grid place-items-center rounded-full border text-white" style={{ borderColor: 'rgba(255,255,255,.25)' }}>
                <Icon className="w-4 h-4" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Sticky side column under the section bar; on screens too short for the
   whole card it scrolls with the page instead of hiding part of it. */
function StickyAside({ children }) {
  const ref = React.useRef(null);
  const [fits, setFits] = React.useState(true);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => setFits(el.offsetHeight <= window.innerHeight - NAV_H - BAR_H - 48);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => { ro.disconnect(); window.removeEventListener('resize', fit); };
  }, []);
  return <aside ref={ref} className={fits ? 'lg:sticky' : ''} style={fits ? { top: NAV_H + BAR_H + 24 } : undefined}>{children}</aside>;
}

export default function ModernOpportunityDetail() {
  const { slug } = useParams();
  const settings = useSettings();
  const skin = opportunitySkin(settings.active_theme);
  const [o, setO] = React.useState(null);
  const [missing, setMissing] = React.useState(false);
  const [lb, setLb] = React.useState(-1);
  const [active, setActive] = React.useState('about');

  React.useEffect(() => {
    setO(null); setMissing(false);
    opportunitiesAPI.detail(slug).then(r => setO(r.data)).catch(() => setMissing(true));
  }, [slug]);

  const sections = o ? [
    o.description && { id: 'about', label: 'Overview' },
    ((o.backers || []).length + (o.services || []).length + (o.benefits || []).length > 0) && { id: 'offerings', label: 'Offerings' },
    { id: 'investors', label: 'Investors' },
    { id: 'backers', label: 'Backers' },
    (o.updates || []).length > 0 && { id: 'updates', label: 'Updates' },
    (o.faq || []).some(t => (t.questions || []).length) && { id: 'faq', label: 'FAQ' },
    (o.team || []).length > 0 && { id: 'team', label: 'Team' },
    o.author_profile && { id: 'author', label: 'Author' },
    { id: 'questions', label: 'Questions' },
  ].filter(Boolean) : [];
  const patch = (p) => setO(prev => ({ ...prev, ...p }));
  const ids = sections.map(s => s.id).join(',');

  // Underline the section the reader is in (the last one whose top passed the bar).
  React.useEffect(() => {
    if (!ids) return undefined;
    const els = ids.split(',').map(id => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    setActive(els[0].id);
    const io = new IntersectionObserver(entries => {
      const passed = els.filter(el => el.getBoundingClientRect().top <= NAV_H + BAR_H + 40);
      if (passed.length) setActive(passed[passed.length - 1].id);
      else if (entries.length) setActive(els[0].id);
    }, { rootMargin: `-${NAV_H + BAR_H + 30}px 0px -55% 0px`, threshold: [0, 1] });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [ids]);

  if (missing) {
    return (
      <div className="min-h-screen grid place-items-center bg-white px-6" style={skin}>
        <div className="text-center">
          <p className="mb-4" style={{ color: 'var(--color-body-text, #475569)' }}>Opportunity not found.</p>
          <Link to="/opportunities" className="mx-link font-bold text-sm" style={{ color: 'var(--op-strong)' }}>Back to opportunities</Link>
        </div>
      </div>
    );
  }
  if (!o) {
    return (
      <div className={`${shell} bg-white pt-32 pb-20 flex flex-col gap-4`} aria-busy="true">
        <div className="h-4 w-48 rounded animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />
        <div className="h-12 w-3/4 rounded animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />
        <div className="h-[clamp(240px,38vw,480px)] rounded-[var(--op-radius)] animate-pulse mt-4" style={{ background: 'rgba(20,26,58,.06)' }} />
      </div>
    );
  }

  const images = (o.images || []).filter(Boolean);
  const location = [o.geo?.city, o.geo?.state, o.geo?.country].filter(Boolean).join(', ');
  const eyebrow = [o.type_name, location].filter(Boolean).join('  /  ');
  const lbItems = images.map((src, i) => ({ id: i, image: src, title: o.name }));

  return (
    <div className="bg-white" style={skin} data-testid="opportunity-detail">
      <style>{CSS}</style>
      <header className={`${shell} ${IS_FOREIGN_HOST ? 'pt-14 md:pt-16' : 'pt-28 md:pt-32'} pb-6 flex flex-col gap-3`}>
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-[13px] font-semibold" style={{ color: faint }}>
          <Link to="/" className="mx-link" style={{ color: 'var(--color-heading, #1a2332)' }}>Home</Link><span aria-hidden="true">/</span>
          <Link to="/opportunities" className="mx-link" style={{ color: 'var(--color-heading, #1a2332)' }}>Opportunities</Link><span aria-hidden="true">/</span>
          <span className="truncate max-w-[50vw]">{o.name}</span>
        </nav>
        {eyebrow && <span className="text-[11px] font-bold uppercase whitespace-pre" style={{ letterSpacing: '.16em', color: 'var(--op-strong)' }}>{eyebrow}</span>}
        <h1 className="font-bold max-w-[26ch]" style={{ fontFamily: HEAD, fontSize: 'clamp(2rem, 3.8vw, 3.1rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="opportunity-title">{o.name}</h1>
        {plain(o.summary) && <p className="text-base md:text-lg leading-relaxed max-w-[68ch]" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(o.summary)}</p>}
      </header>

      <div className={shell}>
        <Mosaic images={images} open={!!o.funding_open} name={o.name} fallback={o.type_default_image} onOpen={setLb} />
        <KeyFigures o={o} />
      </div>

      {sections.length > 0 && <SectionBar sections={sections} active={active} />}

      <div className={`${shell} py-12 lg:py-16 grid gap-12 lg:gap-16 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] items-start`}>
        <div className="min-w-0 flex flex-col gap-14">
          {o.description && (
            <section id="about"><SectionTitle>About this opportunity</SectionTitle><Rich html={o.description} /></section>
          )}
          {sections.some(s => s.id === 'offerings') && (
            <section id="offerings"><SectionTitle>Offerings</SectionTitle><TierTabs o={o} /></section>
          )}
          <section id="investors"><SectionTitle>Investors</SectionTitle><PeopleList people={o.investors || []} empty="No investors yet." /></section>
          <section id="backers"><SectionTitle>Backers</SectionTitle><PeopleList people={o.backers_list || []} empty="No backers yet." /></section>
          {(o.updates || []).length > 0 && (
            <section id="updates">
              <SectionTitle>Updates</SectionTitle>
              <ol className="border-l-2 pl-6 flex flex-col gap-6" style={{ borderColor: line }}>
                {o.updates.map(u => (
                  <li key={u.id} className="relative">
                    <span className="absolute -left-[31px] top-1.5 w-2.5 h-2.5 rounded-full" style={{ background: 'var(--color-accent, #ff9c1b)' }} />
                    {day(u.created_at) && <span className="block text-xs font-semibold" style={{ color: faint }}>{day(u.created_at)}</span>}
                    <span className="block font-bold mt-0.5 mb-1" style={{ color: 'var(--color-heading, #1a2332)' }}>{u.title}</span>
                    <Rich html={u.description} className="mod-prose !text-[15px] !leading-relaxed" />
                  </li>
                ))}
              </ol>
            </section>
          )}
          {sections.some(s => s.id === 'faq') && (
            <section id="faq">
              <SectionTitle>FAQ</SectionTitle>
              {o.faq.filter(t => (t.questions || []).length).map(t => (
                <div key={t.topic_id} className="mb-6 last:mb-0">
                  {t.topic && <h3 className="text-[11px] font-bold uppercase mb-1" style={{ fontFamily: 'var(--op-body)', letterSpacing: '.12em', color: faint }}>{t.topic}</h3>}
                  {t.questions.map(q => (
                    <details key={q.id} className="mod-faq border-b" style={{ borderColor: line }}>
                      <summary className="flex justify-between gap-4 py-3.5 cursor-pointer font-semibold text-[15px]" style={{ color: 'var(--color-heading, #1a2332)' }}>
                        {q.title}<span className="mod-plus text-xl leading-none flex-none" aria-hidden="true" style={{ color: 'var(--op-strong)' }}>+</span>
                      </summary>
                      <div className="pb-4"><Rich html={q.description} className="mod-prose !text-[15px] !leading-relaxed" /></div>
                    </details>
                  ))}
                </div>
              ))}
            </section>
          )}
          {(o.team || []).length > 0 && (
            <section id="team">
              <SectionTitle>Team</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                {o.team.map(m => (
                  <div key={m.member_id} className="flex items-center gap-3">
                    {m.avatar
                      ? <img src={img(m.avatar)} alt="" className="w-12 h-12 rounded-full object-cover" />
                      : <span className="w-12 h-12 rounded-full grid place-items-center text-sm font-bold text-white" style={{ background: 'var(--color-heading, #1a2332)' }}>{`${(m.first_name || '')[0] || ''}${(m.last_name || '')[0] || ''}`.toUpperCase() || '?'}</span>}
                    <div>
                      <div className="font-semibold" style={{ color: 'var(--color-heading, #1a2332)' }}>{m.first_name} {m.last_name}</div>
                      {m.role && <div className="text-sm" style={{ color: faint }}>{m.role}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {o.author_profile && (
            <section id="author"><SectionTitle>Author</SectionTitle>
              <AuthorBlock o={o} hasTeam={(o.team || []).length > 0} hasFaq={sections.some(s => s.id === 'faq')} /></section>
          )}
          <section id="questions"><SectionTitle>Ask a question</SectionTitle><Questions o={o} onChange={patch} /></section>
        </div>
        <StickyAside><TermCard o={o} /><ActionRow o={o} onChange={patch} /><FundItPanel o={o} /></StickyAside>
      </div>

      {lb >= 0 && (
        <Lightbox items={lbItems} currentIndex={lb} onClose={() => setLb(-1)}
          onNext={() => setLb(i => (i + 1) % lbItems.length)} onPrev={() => setLb(i => (i - 1 + lbItems.length) % lbItems.length)} />
      )}
    </div>
  );
}
